import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Pencil, MapPin } from "lucide-react";

function EditProfileModal({ profile, onClose, onSaved }) {
    const [form, setForm] = useState({
        name: profile.name || "",
        bio: profile.bio || "",
        location: profile.location || "",
        avatar_url: profile.avatar_url || "",
        cover_url: profile.cover_url || "",
        ride_type: profile.ride_type || "both",
    });
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState("");
    const update = (k) => (e) => setForm({ ...form, [k]: e.target.value });

    const submit = async (e) => {
        e.preventDefault();
        setSubmitting(true);
        setError("");
        try {
            const { data } = await api.patch("/users/me", form);
            onSaved(data);
            onClose();
        } catch (err) {
            setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <div
            className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm px-4"
            onClick={onClose}
            data-testid="edit-profile-modal"
        >
            <form
                onClick={(e) => e.stopPropagation()}
                onSubmit={submit}
                className="bg-[var(--crewz-bg)] border border-zinc-800 rounded-sm w-full max-w-lg p-6"
            >
                <p className="overline">Profile</p>
                <h3 className="font-display text-2xl font-bold tracking-tight text-white mt-2">
                    Edit your profile
                </h3>
                <div className="mt-5 space-y-3">
                    <input
                        value={form.name}
                        onChange={update("name")}
                        placeholder="Display name"
                        className="w-full bg-zinc-900 border border-zinc-800 focus:border-red-600 rounded-sm px-3 py-2 text-sm font-body text-white outline-none"
                        data-testid="profile-name-input"
                    />
                    <textarea
                        value={form.bio}
                        onChange={update("bio")}
                        placeholder="Bio (your story, what you ride)"
                        rows={3}
                        className="w-full bg-zinc-900 border border-zinc-800 focus:border-red-600 rounded-sm px-3 py-2 text-sm font-body text-white outline-none resize-none"
                        data-testid="profile-bio-input"
                    />
                    <input
                        value={form.location}
                        onChange={update("location")}
                        placeholder="Location"
                        className="w-full bg-zinc-900 border border-zinc-800 focus:border-red-600 rounded-sm px-3 py-2 text-sm font-body text-white outline-none"
                        data-testid="profile-location-input"
                    />
                    <input
                        value={form.avatar_url}
                        onChange={update("avatar_url")}
                        placeholder="Avatar image URL"
                        className="w-full bg-zinc-900 border border-zinc-800 focus:border-red-600 rounded-sm px-3 py-2 text-sm font-body text-white outline-none"
                        data-testid="profile-avatar-input"
                    />
                    <input
                        value={form.cover_url}
                        onChange={update("cover_url")}
                        placeholder="Cover image URL"
                        className="w-full bg-zinc-900 border border-zinc-800 focus:border-red-600 rounded-sm px-3 py-2 text-sm font-body text-white outline-none"
                        data-testid="profile-cover-input"
                    />
                    <select
                        value={form.ride_type}
                        onChange={update("ride_type")}
                        className="w-full bg-zinc-900 border border-zinc-800 focus:border-red-600 rounded-sm px-3 py-2 text-sm font-body text-white outline-none"
                        data-testid="profile-ride-type-select"
                    >
                        <option value="both">Cars & Bikes</option>
                        <option value="cars">Cars</option>
                        <option value="bikes">Bikes</option>
                    </select>
                </div>
                {error && <p className="text-xs text-red-500 font-body mt-3">{error}</p>}
                <div className="flex items-center justify-end gap-2 mt-5">
                    <button
                        type="button"
                        onClick={onClose}
                        className="px-4 py-2 text-sm text-zinc-400 hover:text-white"
                        data-testid="profile-cancel-btn"
                    >
                        Cancel
                    </button>
                    <button
                        type="submit"
                        disabled={submitting}
                        className="bg-red-600 hover:bg-red-700 text-white px-4 py-2 rounded-sm font-semibold tracking-wide text-sm transition-colors disabled:opacity-50"
                        data-testid="profile-save-btn"
                    >
                        {submitting ? "Saving…" : "Save"}
                    </button>
                </div>
            </form>
        </div>
    );
}

export default function Profile() {
    const { username } = useParams();
    const { user, refresh } = useAuth();
    const [profile, setProfile] = useState(null);
    const [loading, setLoading] = useState(true);
    const [editing, setEditing] = useState(false);

    const load = async () => {
        try {
            const { data } = await api.get(`/users/${username}`);
            setProfile(data);
        } catch (_e) {
            setProfile(false);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        load();
    }, [username]); // eslint-disable-line

    if (loading) {
        return (
            <div className="min-h-screen bg-[var(--crewz-bg)]">
                <Navbar />
                <div className="max-w-4xl mx-auto px-6 py-20 text-zinc-500 font-body">
                    Loading profile…
                </div>
            </div>
        );
    }
    if (!profile) {
        return (
            <div className="min-h-screen bg-[var(--crewz-bg)]">
                <Navbar />
                <div className="max-w-4xl mx-auto px-6 py-20 text-zinc-300 font-body">
                    Profile not found.{" "}
                    <Link to="/feed" className="text-red-500 hover:text-red-400">
                        Back to feed
                    </Link>
                </div>
            </div>
        );
    }

    const isMe = user && user.username === profile.username;

    return (
        <div className="min-h-screen bg-[var(--crewz-bg)]" data-testid={`profile-page-${profile.username}`}>
            <Navbar />
            <div className="relative h-56 md:h-72 overflow-hidden border-b border-white/5">
                {profile.cover_url ? (
                    <img
                        src={profile.cover_url}
                        alt="cover"
                        className="w-full h-full object-cover opacity-50"
                    />
                ) : (
                    <div className="w-full h-full bg-gradient-to-br from-zinc-900 to-zinc-950" />
                )}
                <div className="absolute inset-0 bg-gradient-to-t from-[var(--crewz-bg)] to-transparent" />
            </div>

            <main className="max-w-4xl mx-auto px-6 -mt-16 relative">
                <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4">
                    <div className="flex items-end gap-4">
                        <div className="w-28 h-28 rounded-sm bg-zinc-900 border-4 border-[var(--crewz-bg)] flex items-center justify-center overflow-hidden">
                            {profile.avatar_url ? (
                                <img
                                    src={profile.avatar_url}
                                    alt={profile.username}
                                    className="w-full h-full object-cover"
                                />
                            ) : (
                                <span className="font-display text-4xl font-black text-zinc-500">
                                    {profile.username?.[0]?.toUpperCase()}
                                </span>
                            )}
                        </div>
                        <div className="pb-2">
                            <h1 className="font-display text-3xl md:text-4xl font-black tracking-tighter text-white">
                                {profile.name}
                            </h1>
                            <p className="font-body text-sm text-zinc-500">@{profile.username}</p>
                        </div>
                    </div>
                    {isMe && (
                        <button
                            onClick={() => setEditing(true)}
                            className="inline-flex items-center gap-1.5 bg-zinc-900 border border-zinc-800 hover:bg-zinc-800 text-zinc-200 px-4 py-2 rounded-sm font-semibold text-sm tracking-wide transition-colors"
                            data-testid="edit-profile-btn"
                        >
                            <Pencil size={14} /> Edit Profile
                        </button>
                    )}
                </div>

                <div className="mt-6 grid grid-cols-1 md:grid-cols-3 gap-6 pb-16">
                    <div className="md:col-span-2 bg-zinc-900/40 border border-zinc-800/50 rounded-sm p-6">
                        <p className="overline mb-3">Bio</p>
                        <p className="font-body text-zinc-300 leading-relaxed whitespace-pre-wrap">
                            {profile.bio || "No bio yet."}
                        </p>
                        <div className="flex flex-wrap gap-4 mt-4 text-sm text-zinc-500 font-body">
                            {profile.location && (
                                <span className="inline-flex items-center gap-1.5">
                                    <MapPin size={14} /> {profile.location}
                                </span>
                            )}
                            <span className="inline-flex items-center gap-1.5 overline">
                                {profile.ride_type === "cars"
                                    ? "Drives"
                                    : profile.ride_type === "bikes"
                                      ? "Rides"
                                      : "Drives & Rides"}
                            </span>
                        </div>
                    </div>
                    <div className="bg-zinc-900/40 border border-zinc-800/50 rounded-sm p-6">
                        <p className="overline mb-4">Stats</p>
                        <div className="space-y-3 font-body">
                            <div className="flex items-center justify-between">
                                <span className="text-sm text-zinc-400">Posts</span>
                                <span className="font-display text-2xl font-black text-white">
                                    {profile.post_count || 0}
                                </span>
                            </div>
                            <div className="flex items-center justify-between">
                                <span className="text-sm text-zinc-400">Photos</span>
                                <span className="font-display text-2xl font-black text-white">
                                    {profile.media_count || 0}
                                </span>
                            </div>
                        </div>
                    </div>
                </div>
            </main>
            <Footer />

            {editing && (
                <EditProfileModal
                    profile={profile}
                    onClose={() => setEditing(false)}
                    onSaved={(updated) => {
                        setProfile((p) => ({ ...p, ...updated }));
                        refresh();
                    }}
                />
            )}
        </div>
    );
}
